import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CUSTOM_PRIVATE_SHARE_CARD_TITLE,
  CUSTOM_PRIVATE_SHARE_FILE_PREFIX,
  CUSTOM_PRIVATE_SHARE_TEXT,
  CUSTOM_STYLE_GRID_5,
  CUSTOM_STYLE_OPEN_ENDED,
} from '../../../constants/games/zoomedInCustomGame';
import { createCustomPrivateShareCardBlob } from '../../../utils/customPrivateShareCard';
import { shareOrDownloadImageBlob } from '../../../utils/zoomedImageShare';
import './CustomPrivateShareCardModal.css';

const buildFileName = (gameSlug) => {
  const safeSlug = String(gameSlug || '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (safeSlug) return `${CUSTOM_PRIVATE_SHARE_FILE_PREFIX}-${safeSlug}.png`;
  return `${CUSTOM_PRIVATE_SHARE_FILE_PREFIX}.png`;
};

const buildProxyImageUrl = (gameSlug) => {
  const slug = String(gameSlug || '').trim().toLowerCase();
  if (!slug) return '';
  return `/api/customShareImage?slug=${encodeURIComponent(slug)}`;
};

const isRetryableCardImageError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('failed to load image') ||
    message.includes('securityerror') ||
    message.includes('taint') ||
    message.includes('failed to export image')
  );
};

const CopyIcon = () => (
  <svg
    className="custom-private-share-modal__action-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <rect x="4" y="4" width="11" height="11" rx="2" />
  </svg>
);

const ShareIcon = () => (
  <svg
    className="custom-private-share-modal__action-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v10" />
    <path d="m8 7 4-4 4 4" />
    <path d="M5 13v7h14v-7" />
  </svg>
);

const SaveImageIcon = () => (
  <svg
    className="custom-private-share-modal__action-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 4v10" />
    <path d="m8 10 4 4 4-4" />
    <path d="M5 19h14" />
  </svg>
);

const CustomPrivateShareCardModal = ({
  imageUrl,
  shareZoomLevel,
  shareZoomOrigin,
  guesses,
  solution,
  isGridStyle = false,
  maxGuesses,
  gameSlug = '',
  onClose,
  onHome,
}) => {
  const [cardBlob, setCardBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [showGuessGrid, setShowGuessGrid] = useState(isGridStyle);
  const [neutralGuessColors, setNeutralGuessColors] = useState(false);
  const [successAction, setSuccessAction] = useState('');
  const [successActionLabel, setSuccessActionLabel] = useState('');
  const [error, setError] = useState('');
  const successTimerRef = useRef(null);

  const style = isGridStyle ? CUSTOM_STYLE_GRID_5 : CUSTOM_STYLE_OPEN_ENDED;
  const fileName = useMemo(() => buildFileName(gameSlug), [gameSlug]);
  const proxyImageUrl = useMemo(() => buildProxyImageUrl(gameSlug), [gameSlug]);

  const canCopyImage = useMemo(
    () =>
      Boolean(
        window?.isSecureContext &&
          navigator?.clipboard?.write &&
          typeof window.ClipboardItem !== 'undefined',
      ),
    [],
  );

  useEffect(() => {
    if (!isGridStyle) {
      setShowGuessGrid(false);
      setNeutralGuessColors(false);
    }
  }, [isGridStyle]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const clearActionSuccess = useCallback(() => {
    if (successTimerRef.current) {
      window.clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    setSuccessAction('');
    setSuccessActionLabel('');
  }, []);

  const showActionSuccess = useCallback(
    (action, label) => {
      clearActionSuccess();
      setSuccessAction(action);
      setSuccessActionLabel(label);
      successTimerRef.current = window.setTimeout(() => {
        setSuccessAction('');
        setSuccessActionLabel('');
        successTimerRef.current = null;
      }, 1800);
    },
    [clearActionSuccess],
  );

  useEffect(() => {
    let isMounted = true;
    const generate = async () => {
      setIsGenerating(true);
      setError('');
      clearActionSuccess();
      setCardBlob(null);

      try {
        const payload = {
          guesses,
          solution,
          includeGuessGrid: isGridStyle && showGuessGrid,
          neutralGuessColors,
          maxGuesses,
          style,
        };

        let blob;
        try {
          blob = await createCustomPrivateShareCardBlob({
            imageUrl,
            zoomLevel: shareZoomLevel,
            zoomOrigin: shareZoomOrigin,
            ...payload,
          });
        } catch (primaryError) {
          const canRetryWithProxy = Boolean(proxyImageUrl) && isRetryableCardImageError(primaryError);
          if (!canRetryWithProxy) throw primaryError;

          // Fallback to same-origin proxy image to avoid CORS/canvas restrictions on some custom uploads.
          blob = await createCustomPrivateShareCardBlob({
            imageUrl: proxyImageUrl,
            zoomLevel: 100,
            zoomOrigin: null,
            ...payload,
          });
        }

        if (!isMounted) return;
        setCardBlob(blob);
      } catch (generationError) {
        if (!isMounted) return;
        setError(generationError?.message || 'Failed to generate share card.');
      } finally {
        if (isMounted) {
          setIsGenerating(false);
        }
      }
    };

    generate();

    return () => {
      isMounted = false;
    };
  }, [
    clearActionSuccess,
    guesses,
    imageUrl,
    isGridStyle,
    maxGuesses,
    neutralGuessColors,
    shareZoomLevel,
    shareZoomOrigin,
    showGuessGrid,
    solution,
    style,
    proxyImageUrl,
  ]);

  useEffect(() => {
    if (!cardBlob) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(cardBlob);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [cardBlob]);

  const handleHome = useCallback(() => {
    if (typeof onHome === 'function') {
      onHome();
      return;
    }
    onClose?.();
  }, [onClose, onHome]);

  const handleCopy = useCallback(async () => {
    if (!cardBlob || busyAction) return;
    setBusyAction('copy');
    setError('');
    clearActionSuccess();

    try {
      if (!canCopyImage) {
        throw new Error(
          window?.isSecureContext
            ? 'Copy image is not supported in this browser.'
            : 'Copy image needs https (or localhost). Use Save Image instead.',
        );
      }

      const textBlob = new Blob([CUSTOM_PRIVATE_SHARE_TEXT], { type: 'text/plain' });
      const item = new window.ClipboardItem({
        'image/png': cardBlob,
        'text/plain': textBlob,
      });
      await navigator.clipboard.write([item]);
      showActionSuccess('copy', 'Copied!');
    } catch (copyError) {
      setError(copyError?.message || 'Failed to copy image.');
    } finally {
      setBusyAction('');
    }
  }, [busyAction, canCopyImage, cardBlob, clearActionSuccess, showActionSuccess]);

  const handleShare = useCallback(async () => {
    if (!cardBlob || busyAction) return;
    setBusyAction('share');
    setError('');
    clearActionSuccess();

    try {
      const result = await shareOrDownloadImageBlob({
        blob: cardBlob,
        fileName,
        shareTitle: CUSTOM_PRIVATE_SHARE_CARD_TITLE,
        shareText: CUSTOM_PRIVATE_SHARE_TEXT,
      });

      showActionSuccess('share', result?.method === 'share' ? 'Shared!' : 'Saved!');
    } catch (shareError) {
      setError(shareError?.message || 'Failed to share image.');
    } finally {
      setBusyAction('');
    }
  }, [busyAction, cardBlob, clearActionSuccess, fileName, showActionSuccess]);

  const handleSaveImage = useCallback(async () => {
    if (!cardBlob || busyAction) return;
    setBusyAction('save');
    setError('');
    clearActionSuccess();

    try {
      if (typeof window.showSaveFilePicker === 'function') {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [
            {
              description: 'PNG image',
              accept: { 'image/png': ['.png'] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(cardBlob);
        await writable.close();
        showActionSuccess('save', 'Saved!');
        return;
      }

      const file = new File([cardBlob], fileName, {
        type: cardBlob.type || 'image/png',
      });
      if (navigator?.share && navigator?.canShare?.({ files: [file] })) {
        await navigator.share({
          title: 'Save image',
          files: [file],
        });
        showActionSuccess('save', 'Done!');
        return;
      }

      const url = URL.createObjectURL(cardBlob);
      try {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(url);
      }

      showActionSuccess('save', 'Saved!');
    } catch (saveError) {
      if (saveError?.name === 'AbortError') {
        return;
      }
      setError(saveError?.message || 'Failed to save image.');
    } finally {
      setBusyAction('');
    }
  }, [busyAction, cardBlob, clearActionSuccess, fileName, showActionSuccess]);

  return (
    <div className="modal-overlay">
      <div className="modal-content game-result-modal custom-private-share-modal">
        <h2>Share without revealing answers</h2>
        <p className="custom-private-share-modal__subtitle">
          Share a clean Custom card with the image and color pattern only.
        </p>

        <div className="custom-private-share-modal__preview-wrap">
          {isGenerating && (
            <div className="loading-spinner">
              <div className="spinner" />
              <span className="loading-text">Generating share card...</span>
            </div>
          )}

          {!isGenerating && previewUrl && (
            <img
              src={previewUrl}
              alt="Custom private share card preview"
              className="custom-private-share-modal__preview-image"
            />
          )}
        </div>

        {isGridStyle ? (
          <div className="custom-private-share-modal__options">
            <label className="custom-private-share-modal__option">
              <input
                type="checkbox"
                checked={showGuessGrid}
                onChange={(event) => setShowGuessGrid(event.target.checked)}
                disabled={Boolean(busyAction)}
              />
              <span>Show guess grid</span>
            </label>
            <label className="custom-private-share-modal__option">
              <input
                type="checkbox"
                checked={neutralGuessColors}
                onChange={(event) => setNeutralGuessColors(event.target.checked)}
                disabled={!showGuessGrid || Boolean(busyAction)}
              />
              <span>Use neutral colors</span>
            </label>
          </div>
        ) : (
          <div className="custom-private-share-modal__image-only-note">
            Open-ended mode shares image-only cards to keep answers private.
          </div>
        )}

        <div className="custom-private-share-modal__actions">
          <button
            type="button"
            className="custom-private-share-modal__action-btn"
            onClick={handleCopy}
            disabled={!cardBlob || Boolean(busyAction)}
          >
            {busyAction === 'copy' ? (
              'Copying...'
            ) : successAction === 'copy' ? (
              successActionLabel || 'Done!'
            ) : (
              <span className="custom-private-share-modal__action-btn-inner">
                <CopyIcon />
                <span>Copy</span>
              </span>
            )}
          </button>
          <button
            type="button"
            className="custom-private-share-modal__action-btn"
            onClick={handleShare}
            disabled={!cardBlob || Boolean(busyAction)}
          >
            {busyAction === 'share' ? (
              'Sharing...'
            ) : successAction === 'share' ? (
              successActionLabel || 'Done!'
            ) : (
              <span className="custom-private-share-modal__action-btn-inner">
                <ShareIcon />
                <span>Share</span>
              </span>
            )}
          </button>
          <button
            type="button"
            className="custom-private-share-modal__action-btn"
            onClick={handleSaveImage}
            disabled={!cardBlob || Boolean(busyAction)}
          >
            {busyAction === 'save' ? (
              'Saving...'
            ) : successAction === 'save' ? (
              successActionLabel || 'Done!'
            ) : (
              <span className="custom-private-share-modal__action-btn-inner">
                <SaveImageIcon />
                <span>Save Image</span>
              </span>
            )}
          </button>
        </div>

        <div className="custom-private-share-modal__nav">
          <button
            type="button"
            className="custom-private-share-modal__nav-btn custom-private-share-modal__nav-btn--secondary"
            onClick={onClose}
            disabled={Boolean(busyAction)}
          >
            Back
          </button>
          <button
            type="button"
            className="custom-private-share-modal__nav-btn custom-private-share-modal__nav-btn--primary"
            onClick={handleHome}
            disabled={Boolean(busyAction)}
          >
            Home
          </button>
        </div>

        {error && <p className="custom-private-share-modal__error">{error}</p>}
      </div>
    </div>
  );
};

export default CustomPrivateShareCardModal;
